import { type RouteObject, redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";

const nfcLayout = async () => {
  const { CompanyNfcPage } = await import("../../pages/(home)/(company)/nfc");
  return { Component: CompanyNfcPage };
};

const nfcChipsTab = async () => {
  const { NfcChipsTab } = await import(
    "../../pages/(home)/(company)/nfc/list-chips"
  );
  return { Component: NfcChipsTab };
};

const nfcFormsTab = async () => {
  const { NfcFormsTab } = await import(
    "../../pages/(home)/(company)/nfc/list-forms"
  );
  return { Component: NfcFormsTab };
};

const nfcAnalyticsTab = async () => {
  const { NfcAnalyticsTab } = await import(
    "../../pages/(home)/(company)/nfc/list-analytics"
  );
  return { Component: NfcAnalyticsTab };
};

const nfcSubmissionsTab = async () => {
  const { NfcSubmissionsTab } = await import(
    "../../pages/(home)/(company)/nfc/list-submissions"
  );
  return { Component: NfcSubmissionsTab };
};

const nfcSubmissionDetailPage = async () => {
  const { NfcSubmissionDetailPage } = await import(
    "../../pages/(home)/(company)/nfc/submission-detail"
  );
  return { Component: NfcSubmissionDetailPage };
};

const nfcChipDetailPage = async () => {
  const { NfcChipDetailPage } = await import(
    "../../pages/(home)/(company)/nfc/chip-detail"
  );
  return { Component: NfcChipDetailPage };
};

export const DYNAMIC_NFC_SUBMISSION_ID_KEY = "submissionId";
export const DYNAMIC_NFC_CHIP_ID_KEY = "chipId";

export enum COMPANY_NFC_PATH_SEGMENTS {
  ROOT = "nfc",
  CHIPS = "chips",
  FORMS = "forms",
  ANALYTICS = "analytics",
  SUBMISSIONS = "submissions",
  SUBMISSION_DETAIL = `submissions/:${DYNAMIC_NFC_SUBMISSION_ID_KEY}`,
  CHIP_DETAIL = `chips/:${DYNAMIC_NFC_CHIP_ID_KEY}`,
}

export const COMPANY_NFC_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_NFC_PATH_SEGMENTS.ROOT)),
  CHIPS: withCompanyPrefix(
    addSlashes(COMPANY_NFC_PATH_SEGMENTS.ROOT, "chips"),
  ),
  FORMS: withCompanyPrefix(
    addSlashes(COMPANY_NFC_PATH_SEGMENTS.ROOT, "forms"),
  ),
  ANALYTICS: withCompanyPrefix(
    addSlashes(COMPANY_NFC_PATH_SEGMENTS.ROOT, "analytics"),
  ),
  SUBMISSIONS: withCompanyPrefix(
    addSlashes(COMPANY_NFC_PATH_SEGMENTS.ROOT, "submissions"),
  ),
  SUBMISSION_DETAIL: (submissionId: number | string) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_NFC_PATH_SEGMENTS.ROOT,
        "submissions",
        String(submissionId),
      ),
    ),
  CHIP_DETAIL: (chipId: number | string) =>
    withCompanyPrefix(
      addSlashes(COMPANY_NFC_PATH_SEGMENTS.ROOT, "chips", String(chipId)),
    ),
});

export const companyNfcRoutes: RouteObject = {
  path: COMPANY_NFC_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "NFC" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      lazy: nfcLayout,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_NFC_PATHS.CHIPS),
        },
        {
          path: COMPANY_NFC_PATH_SEGMENTS.CHIPS,
          handle: { breadcrumb: "Assigned Chips" },
          lazy: nfcChipsTab,
        },
        {
          // Read-only forms list. Authoring (create/edit) hands off to
          // Studio → Forms from within the table.
          path: COMPANY_NFC_PATH_SEGMENTS.FORMS,
          handle: { breadcrumb: "Forms" },
          lazy: nfcFormsTab,
        },
        {
          path: COMPANY_NFC_PATH_SEGMENTS.ANALYTICS,
          handle: { breadcrumb: "Analytics" },
          lazy: nfcAnalyticsTab,
        },
        {
          path: COMPANY_NFC_PATH_SEGMENTS.SUBMISSIONS,
          handle: { breadcrumb: "Submissions" },
          lazy: nfcSubmissionsTab,
        },
      ],
    },

    // ── Standalone routes (no stats / tabs chrome) ───────────────────────────
    {
      path: COMPANY_NFC_PATH_SEGMENTS.SUBMISSION_DETAIL,
      handle: { breadcrumb: "Submission Detail" },
      lazy: nfcSubmissionDetailPage,
    },
    {
      path: COMPANY_NFC_PATH_SEGMENTS.CHIP_DETAIL,
      handle: { breadcrumb: "Chip Detail" },
      lazy: nfcChipDetailPage,
    },
  ],
};
